import { Component, OnInit, OnDestroy, Input, SimpleChanges, OnChanges, Output, EventEmitter } from '@angular/core';
import { RadioButtonValue } from '@slb-dls/angular-material/radio-button-group';
import { ZoneSettingsConstants } from '../../shared/constants/zone-setting-constants';
import {
  RigProfile,
  UpdateByDetail,
  ZoneConfig,
  ZoneSetting,
  ZoneSettingCommandStatusResponse,
  ZoneSettingOptions,
} from '../../shared/models/zoneSetting';
import { ZoneService } from '../../shared/services/zone.service';
import { EMPTY, Observable, Subject, catchError, delay, expand, of, switchMap, takeUntil, tap, timer } from 'rxjs';
import { MessageService, SlbSeverity } from '@slb-dls/angular-material/notification';
import { LoginService } from '@agora/agora-ui-library';
import { UserSettingsService } from '../../shared/services/user-settings.service';
import { UnitsData } from '../../shared/models/unit';
import { CameraProfileService } from '../../shared/services/camera-profile.service';
import { ZoneConfiguration } from '../../shared/models/zone-configuration';
import { RIG_PROFILES, ZoneConfigurationConstants } from '../../shared/constants/zone-config.constant';

@Component({
  selector: 'app-zone-setting',
  templateUrl: './zone-setting.component.html',
  styleUrls: ['./zone-setting.component.scss'],
})
export class ZoneSettingComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly AUTHORIZED_PEOPLE_COUNT_TAG = 'AuthorizedPeopleCount_raw';

  @Input() workflow: string;
  @Input() zoneName: string;
  @Input() ppeEventsOptions: any;
  @Input() previewError: string;
  @Input() cameraStatus: string;
  @Input() zoneConfiguration: ZoneConfiguration;
  @Input() profileTypeMasterId: string;
  @Input() rigProfiles: RigProfile[];

  @Output() zoneSettingChangeEvent = new EventEmitter<string>();
  @Output() updateByDetailEvent = new EventEmitter<UpdateByDetail>();

  public redZoneTypes: RadioButtonValue[] = ZoneSettingsConstants.REDZONETYPE_RADIO_OPTIONS;
  public zoneSettings: ZoneSettingOptions[] = [];
  public currentRedZone: number;
  public zoneId: string;
  public userName: string;
  public userEmail: string;
  public controllerId: string;
  public groupId: string;
  public existingZoneSetting: ZoneSetting;
  public showLoader = false;
  public status: string;
  public peopleCountStatus: string | null = null;
  public zoneSettingFetchError: string | null = null;
  public isValid: boolean[] = [];
  public inactiveGateway: boolean;
  public inactiveCamera: boolean;
  public isValueChange: boolean;
  public isEnableSiren = false;
  public updatedOn: string | null;
  public updatedBy: string;
  public staticModeOn: boolean;
  public profileChanged = false;

  private destroyed = new Subject();
  private unitSelected: UnitsData;
  private init: number;
  private saveEnabled = false;
  private delay = 5000;
  private buLogic: number;

  constructor(
    public zoneService: ZoneService,
    private messageService: MessageService,
    private loginService: LoginService,
    private cameraService: CameraProfileService,
    private userSettingService: UserSettingsService
  ) {
    this.cameraService.selectedZoneId$
      .pipe(
        tap((params: ZoneConfig) => {
          if (params) {
            this.zoneId = params.zoneId;
            this.controllerId = params.deviceId;
            this.currentRedZone = params.redZoneType;
            this.groupId = params.groupID;
            this.buLogic = this.normalizeBuLogic(params.buLogic);
            if (!this.saveEnabled) {
              this.saveEnabled = false;
              this.zoneSettings = this.getBuLogic(this.buLogic);
            }
          }
        }),
        switchMap(() =>
          this.userSettingService.unitSelected$.pipe(
            tap((value: string) => {
              if (value && value !== '') {
                this.unitSelected = JSON.parse(value);
              }
            })
          )
        ),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.rigProfiles.length && (changes['profileTypeMasterId'] || changes['rigProfiles']) && this.profileTypeMasterId?.length) {
      this.getZoneSettings();
      this.profileChanged = true;
    }
    if (changes['zoneConfiguration']?.currentValue?.controllerId.length === 0) {
      this.showLoader = false;
    }
  }

  public ngOnInit(): void {
    this.showLoader = true;
    this.inactiveCamera = false;
    this.inactiveGateway = false;
    this.isValueChange = false;
    this.zoneService.isValueChange = false;
    const gatewayStatus = sessionStorage.getItem('gatewayStatus');
    if (gatewayStatus) {
      this.inactiveGateway = gatewayStatus !== '0' ? true : false;
      if (!this.inactiveGateway) {
        this.inactiveCamera = this.cameraStatus !== '0' ? true : false;
      }
    }
    this.loginService.isUserLoggedIn$
      .pipe(
        tap(() => {
          this.userEmail = this.loginService.getUserInfo()?.email ?? '';
          this.userName = this.loginService.getUserInfo()?.name ?? '';
        })
      )
      .subscribe();
  }

  public getZoneSettings(): void {
    this.showLoader = true;
    this.zoneSettingFetchError = null;
    this.updatedBy = '';
    this.updatedOn = '';
    if (this.rigProfiles?.length) {
      this.zoneService
        .getZoneSetting(this.controllerId, this.profileTypeMasterId)
        .pipe(
          catchError(() => {
            this.showLoader = false;
            this.zoneSettingFetchError = ZoneSettingsConstants.zone_fetchErrorMessage;
            this.messageService.add({
              severity: SlbSeverity.Error,
              summary: ZoneSettingsConstants.zone_fetchErrorMessage,
            });

            return EMPTY;
          }),
          tap((data: ZoneSetting) => {
            if (data) {
              this.zoneSettings = this.getBuLogic(this.buLogic);
              this.showLoader = false;
              this.existingZoneSetting = data;
              this.updatedBy = data.updateBy;
              this.updatedOn = data.updateTime ? data.updateTime : null;
              this.updateByDetailEvent.emit({
                updatedBy: this.updatedBy,
                updatedOn: this.updatedOn,
              });
              this.currentRedZone = data.zoneTypeId;
              this.staticModeOn =
                this.rigProfiles.filter(r => r.id === this.profileTypeMasterId)[0]?.name.toLowerCase() === RIG_PROFILES.STATIC_MODE;
              if (this.staticModeOn) {
                if (data.zoneTypeId !== 0) {
                  this.currentRedZone = 1;
                }
                this.redZoneTypes.filter(rZ => rZ.value === 2)[0].isDisabled = true;
              } else {
                this.redZoneTypes.filter(rZ => rZ.value === 2)[0].isDisabled = false;
              }
              this.updateZoneSetting(data.zoneSettingData);
              this.status = data.commandStatus ?? '';
              this.peopleCountStatus = data.peopleCountStatus ?? null;
              if (Object.keys(data?.zoneSettingData ?? {}).length === 0) {
                this.zoneSettings = this.getBuLogic(this.buLogic);
                this.updateUnits();
              }
            }
          }),
          takeUntil(this.destroyed)
        )
        .subscribe();
    }
  }

  public updateZoneSetting(response: any): void {
    if (response) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      for (const key of Object.keys(response)) {
        if (key !== 'enable_siren') {
          const index = this.zoneSettings.findIndex(x => x.tagname === key);
          if (index !== -1) {
            if (response[key] !== null && typeof response[key] !== 'undefined') {
              this.zoneSettings[index].value = response[key];
            }
          }
        }
      }
      this.updateUnits();
      this.zoneService.isValueChange = false;
    } else {
      this.zoneSettings?.forEach(zone => {
        zone.unit = this.userSettingService.getUnit(zone.unit, this.unitSelected.unitData);
      });
    }
    this.showLoader = false;
  }

  public updateSirenToggle(response: any): void {
    if (response) {
      this.isEnableSiren = response.enable_siren === 0 ? false : true;
    }
  }

  public updateNewSettings(tags: any): any {
    this.zoneSettings.map(zone => {
      if (tags[zone.tagname]) {
        tags[zone.tagname] = zone.value;
      }
    });

    return tags;
  }

  public saveZoneSettings(): void {
    this.showLoader = true;
    const buId = this.normalizeBuLogic(this.buLogic);
    const zoneSettingJson = this.getBuLogic_save(buId) as Record<string, number>;

    this.zoneSettings.forEach((element, index) => {
      const key = element.tagname;
      if (!Object.prototype.hasOwnProperty.call(zoneSettingJson, key)) {
        return;
      }
      this.checkZoneSettingValid(Number(element.zoneUpdatedValue), index);
      if (key === ZoneSettingComponent.AUTHORIZED_PEOPLE_COUNT_TAG) {
        const n = Math.round(Number(element.zoneUpdatedValue));
        zoneSettingJson[key] = Number.isFinite(n) ? Math.min(9, Math.max(0, n)) : 0;
      } else {
        zoneSettingJson[key] = this.userSettingService.conversionToSIUnits(Number(element.zoneUpdatedValue), element.unit);
      }
    });

    const existingData = this.existingZoneSetting?.zoneSettingData as Record<string, number> | undefined;
    const zoneSettingData: Record<string, number> = {
      ...(existingData && typeof existingData === 'object' ? { ...existingData } : {}),
      ...zoneSettingJson,
    };
    zoneSettingData[ZoneSettingComponent.AUTHORIZED_PEOPLE_COUNT_TAG] = this.resolveAuthorizedPeopleCountRaw();

    const zoneSettingRequest = {
      id: this.zoneId,
      equipmentID: this.controllerId,
      profileTypeMasterId: this.profileTypeMasterId,
      zoneTypeId: this.currentRedZone,
      zoneSettingData,
      updateBy: this.userEmail,
      groupID: this.groupId,
      buLogic: buId,
    } as ZoneSetting;

    if (this.isValid || this.profileChanged) {
      this.zoneService
        .saveZoneSetting(zoneSettingRequest)
        .pipe(
          tap(_response => {
            if (_response) {
              this.zoneService.isValueChange = false;
              this.saveEnabled = true;
              const zoneData = {
                deviceId: this.controllerId,
                groupID: this.groupId,
                zoneId: _response.id,
                redZoneType: _response.zoneTypeId,
                buLogic: this.buLogic,
              } as ZoneConfig;
              this.cameraService.setZoneId(zoneData);
              sessionStorage.setItem('config', JSON.stringify(zoneData));
              this.showLoader = false;
              this.messageService.add({
                severity: SlbSeverity.Success,
                summary: 'Success',
                detail: ZoneConfigurationConstants.ZONESETTINGSUCCESS,
              });
            }
            this.getCommandStatus();
            this.profileChanged = false;
          }),
          catchError(err => {
            this.showLoader = false;
            if (err) {
              this.messageService.add({
                severity: SlbSeverity.Info,
                summary: 'ERROR',
                detail: ZoneSettingsConstants.zone_errorMessage,
              });
              this.showLoader = false;
            }

            return of<string>('');
          }),
          takeUntil(this.destroyed)
        )
        .subscribe();
    }
  }

  onZoneSettingChange(): void {
    if (this.currentRedZone === 1) {
      this.profileTypeMasterId = this.rigProfiles.filter(r => r.name.toLowerCase() === RIG_PROFILES.STATIC_MODE)[0].id;
      if (this.profileTypeMasterId) {
        this.zoneSettingChangeEvent.emit(this.profileTypeMasterId);
      } else {
        this.zoneSettingChangeEvent.emit('');
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
  }

  public checkZoneSettingValid(event: number | string | null | undefined, index: number): void {
    this.zoneService.isValueChange = true;
    const tagname = this.zoneSettings[index]?.tagname;
    if (tagname === ZoneSettingComponent.AUTHORIZED_PEOPLE_COUNT_TAG) {
      if (event === null || typeof event === 'undefined' || (typeof event === 'string' && event.trim() === '')) {
        this.isValid[index] = false;

        return;
      }
      const num = Number(event);
      this.isValid[index] = !Number.isNaN(num) && Number.isInteger(num) && num >= 0 && num <= 9;

      return;
    }
    if (event === null || typeof event === 'undefined') {
      this.isValid[index] = false;
    } else {
      const pattern = new RegExp('^-?[0-9]*(?:.[0-9]{0,7})?$');
      const result = pattern.test(event.toString());
      this.isValid[index] = result;
    }
  }

  public enableSirenToggleChange(): void {
    this.zoneService.isValueChange = true;
  }

  public ppeSettingChange(): void {
    this.zoneService.isValueChange = true;
  }

  public ppeLoader(loaderState: boolean): void {
    this.showLoader = loaderState;
  }

  public isPeopleCountStatusError(): boolean {
    const p = (this.peopleCountStatus ?? '').trim().toLowerCase();

    return p === 'error';
  }

  private getCommandStatus(): void {
    this.init = 0;
    this.getZoneApi()
      .pipe(
        expand((result: ZoneSettingCommandStatusResponse) =>
          (!this.shouldStopCommandStatusPoll(result) && this.init <= 15 ? this.getZoneApi() : EMPTY).pipe(delay(this.delay))
        ),
        tap((r: ZoneSettingCommandStatusResponse) => this.applyCommandStatusPoll(r)),
        catchError(err => {
          if (err) {
            this.messageService.add({ severity: SlbSeverity.Error, summary: ZoneSettingsConstants.zone_errorMessage });
          }

          return of<ZoneSettingCommandStatusResponse>({ commandStatus: 'Error', peopleCountStatus: null });
        })
      )
      .subscribe();
  }

  private applyCommandStatusPoll(res: ZoneSettingCommandStatusResponse): void {
    this.status = res.commandStatus ?? '';
    this.peopleCountStatus = res.peopleCountStatus ?? null;
  }

  private shouldStopCommandStatusPoll(res: ZoneSettingCommandStatusResponse): boolean {
    const terminal = (s: string | null | undefined): boolean => {
      const v = s?.toLowerCase() ?? '';

      return v.includes('acknowledged') || v.includes('timeout') || v.includes('error');
    };
    if (!terminal(res.commandStatus)) {
      return false;
    }
    const people = res.peopleCountStatus;
    if (people == null || people === '') {
      return true;
    }

    return terminal(people);
  }

  private getZoneApi(): Observable<ZoneSettingCommandStatusResponse> {
    this.init += 1;
    const req$ = this.zoneService.getCommandStatus(this.controllerId, this.init > 15, this.profileTypeMasterId);
    if (this.init === 1) {
      return timer(this.delay).pipe(switchMap(() => req$));
    }

    return req$;
  }

  private updateUnits(): void {
    if (this.unitSelected?.unitData) {
      this.zoneSettings?.forEach(zone => {
        const siUnit = zone.unit;
        zone.unit = this.userSettingService.getUnit(zone.unit, this.unitSelected.unitData);
        zone.zoneUpdatedValue = this.userSettingService.siToChosenUnit(zone.value, siUnit, zone.unit);
      });
    }
  }

  private resolveAuthorizedPeopleCountRaw(): number {
    const row = this.zoneSettings.find(z => z.tagname === ZoneSettingComponent.AUTHORIZED_PEOPLE_COUNT_TAG);
    const n = row ? Math.round(Number(row.zoneUpdatedValue)) : 0;

    return Number.isFinite(n) ? Math.min(9, Math.max(0, n)) : 0;
  }

  private normalizeBuLogic(value: number | string | null | undefined): 1 | 2 | 3 {
    if (value == null || value === '') {
      return 2;
    }
    const n = Number(value);

    return n === 1 || n === 2 || n === 3 ? n : 2;
  }

  private getBuLogic(buLogic: number): any {
    const id = this.normalizeBuLogic(buLogic);
    if (id === 1) {
      return JSON.parse(JSON.stringify(ZoneSettingsConstants.bu1)) as ZoneSettingOptions[];
    } else if (id === 3) {
      return JSON.parse(JSON.stringify(ZoneSettingsConstants.bu3)) as ZoneSettingOptions[];
    } else {
      return JSON.parse(JSON.stringify(ZoneSettingsConstants.bu2)) as ZoneSettingOptions[];
    }
  }

  private getBuLogic_save(buLogic: number): any {
    const id = this.normalizeBuLogic(buLogic);
    if (id === 1) {
      return JSON.parse(JSON.stringify(ZoneSettingsConstants.BU_logic_1)) as ZoneSettingOptions[];
    } else if (id === 3) {
      return JSON.parse(JSON.stringify(ZoneSettingsConstants.BU_logic_3)) as ZoneSettingOptions[];
    } else {
      return JSON.parse(JSON.stringify(ZoneSettingsConstants.BU_logic_2)) as ZoneSettingOptions[];
    }
  }
}
